import React, { useRef } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import BaseToggle from '../inputs/BaseToggle';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A toggle form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseToggle>} BaseToggleProps
 *
 * @param {BaseFieldRenderProps & BaseToggleProps} fieldRenderProps
 * @returns {React.ReactElement} Toggle form field.
 */
const FormToggle = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    optional,
    id,
    name,
    valid,
    disabled,
    hint,
    value,
    labelStyle,
    wrapperClassName,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  return (
    <FieldWrapper className={wrapperClassName}>
      {label && (
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={editorId}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
          style={labelStyle}
        >
          {label}
        </Label>
      )}
      <BaseToggle
        ref={editorRef}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={editorId}
        name={name}
        disabled={disabled}
        checked={value}
        // HACK
        readOnly={wrapperClassName === 'read-only-field'}
        dataTestId={editorId}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormToggle;
